import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import auctionSuggestionsCompactPreview from '../assets/premium-showcase/auction-suggestions-compact-preview.png'
import auctionSuggestionsPreview from '../assets/premium-showcase/auction-suggestions-preview.png'
import divisionsPreview from '../assets/premium-showcase/divisions-preview.png'
import tradeHelperPreview from '../assets/premium-showcase/trade-helper-preview.png'
import wrestlerInsightPreview from '../assets/premium-showcase/wrestler-insight-preview.png'
import {
  MONTHLY_PREMIUM_PRICE,
  YEARLY_PREMIUM_PRICE,
  formatPremiumPrice
} from '../utils/premiumAccess'

const PAYPAL_BASE_URL = 'https://paypal.me/fantawrestling'
const MIN_PREMIUM_MONTHS = 1
const MAX_PREMIUM_MONTHS = 12
const premiumMonthPresets = [1, 3, 6, 12]

const supportAreas = [
  {
    icon: 'bi-hdd-stack-fill',
    title: 'Hosting e stabilita',
    description: 'Per mantenere il sito veloce, affidabile e sempre online.'
  },
  {
    icon: 'bi-tools',
    title: 'Strumenti di gestione',
    description: 'Per lavorare meglio dietro le quinte e seguire ogni stagione.'
  },
  {
    icon: 'bi-palette-fill',
    title: 'Grafiche e interfaccia',
    description: 'Per rendere Fantawrestling piu curato, leggibile e piacevole.'
  },
  {
    icon: 'bi-stars',
    title: 'Nuove funzionalita',
    description:
      'Il vostro contributo aiutera a trasformare le idee premium in funzioni reali per la community.'
  }
]

const premiumFeatures = [
  {
    title: 'Zero pubblicita',
    description: 'Niente banner o popup durante la navigazione.'
  },
  {
    title: 'Aiuto line-up',
    description:
      'Consigli sui ruoli migliori con punteggio recente, record, quotazione e ultime apparizioni.'
  },
  {
    title: 'Commenti wrestler',
    description:
      'Note rapide del team visibili in creazione fazione, line-up e dettagli.'
  },
  {
    title: 'Aiuto scambi',
    description:
      'Suggerimenti su scambi, svincolati in crescita e wrestler da rimuovere.'
  },
  {
    title: 'Divisioni wrestler',
    description:
      'Info su ruolo e posizione per filtrare, ordinare e leggere meglio il roster.'
  }
]

const premiumShowcaseSlides = [
  {
    title: 'Aiuto scambi',
    description:
      'Vedi uscite rischiose, trend negativi e wrestler piu utili da sostituire.',
    image: tradeHelperPreview
  },
  {
    title: 'Suggerimenti prossima asta',
    description: 'Una lista dei nomi da seguire prima della prossima asta.',
    image: auctionSuggestionsPreview
  },
  {
    title: 'Vista rapida suggerimenti',
    description:
      'Vedi chi sono i nomi migliori che potrebbero farti salire in classifica.',
    image: auctionSuggestionsCompactPreview
  },
  {
    title: 'Divisioni wrestler',
    description:
      'Posizioni direttamente nel roster per leggere meglio quanto vale ogni nome.',
    image: divisionsPreview
  },
  {
    title: 'Insight avanzati wrestler',
    description:
      'Commenti, quotazione, record e trend recenti in una scheda completa.',
    image: wrestlerInsightPreview
  }
]

const formatMonthLabel = months => `${months} ${months === 1 ? 'mese' : 'mesi'}`

const buildPaypalAmountPath = amount => `${amount.toFixed(2)}EUR`
const getBasePremiumAmount = months =>
  Number((months * MONTHLY_PREMIUM_PRICE).toFixed(2))
const getPremiumAmount = months =>
  months === 12 ? YEARLY_PREMIUM_PRICE : getBasePremiumAmount(months)

function Premium () {
  const [selectedMonths, setSelectedMonths] = useState(12)
  const [paymentStarted, setPaymentStarted] = useState(false)
  const [activationMailClicked, setActivationMailClicked] = useState(false)
  const navigate = useNavigate()
  const { currentUser, authLoading } = useAuth()

  const baseAmount = useMemo(
    () => getBasePremiumAmount(selectedMonths),
    [selectedMonths]
  )
  const totalAmount = useMemo(
    () => getPremiumAmount(selectedMonths),
    [selectedMonths]
  )
  const savingsAmount = useMemo(
    () => Number((baseAmount - totalAmount).toFixed(2)),
    [baseAmount, totalAmount]
  )
  const isAnnualPlan = selectedMonths === 12
  const paypalLink = useMemo(
    () => `${PAYPAL_BASE_URL}/${buildPaypalAmountPath(totalAmount)}`,
    [totalAmount]
  )
  const activationMailto = useMemo(() => {
    const subject = encodeURIComponent(
      'Conferma pagamento Premium Fantawrestling'
    )
    const body = encodeURIComponent(
      [
        'Ciao team FantaWrestling,',
        '',
        'ho completato il pagamento Premium e vi chiedo di abilitare il mio account.',
        '',
        `Email account: ${currentUser?.email || ''}`,
        `UID account: ${currentUser?.uid || ''}`,
        `Durata scelta: ${formatMonthLabel(selectedMonths)}`,
        `Importo: ${formatPremiumPrice(totalAmount)}`,
        `Link pagamento usato: ${paypalLink}`,
        '',
        'Grazie.'
      ].join('\n')
    )

    return `mailto:fantafwi@gmail.com?subject=${subject}&body=${body}`
  }, [
    currentUser?.email,
    currentUser?.uid,
    paypalLink,
    selectedMonths,
    totalAmount
  ])

  const handleSelectMonths = months => {
    const normalizedMonths = Math.min(
      MAX_PREMIUM_MONTHS,
      Math.max(MIN_PREMIUM_MONTHS, Number(months) || MIN_PREMIUM_MONTHS)
    )
    setSelectedMonths(normalizedMonths)
  }

  const handleCheckout = () => {
    if (!currentUser) {
      navigate('/auth', {
        state: {
          redirectTo: '/premium',
          authMessage:
            'Accedi o registrati per attivare il tuo abbonamento Premium.'
        }
      })
      return
    }

    setPaymentStarted(true)
    window.open(paypalLink, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className='donations-page'>
      <div className='container py-4 py-lg-5'>
        <section className='donations-hero'>
          <div className='donations-hero__copy'>
            <h1 className='donations-title'>Fantawrestling Premium</h1>
            <p className='donations-lead'>
              Un contributo piccolo, facoltativo e utile per sostenere il
              progetto e sbloccare le future funzioni premium.
            </p>
          </div>

          <div className='donations-panel'>
            <span className='donations-panel__eyebrow'>
              <i className='bi bi-stars'></i>
              Supporto premium
            </span>
            <h2>Scegli il tuo contributo</h2>

            <div className='donations-annual-highlight'>
              <span className='donations-annual-highlight__badge'>
                <i className='bi bi-stars me-2'></i>
                Scelta consigliata
              </span>
              <strong>Premium Annuale: 12 mesi a 5 €</strong>
            </div>

            <div className='donations-plan-builder'>
              <div className='donations-month-grid'>
                {premiumMonthPresets.map(months => (
                  <button
                    key={months}
                    type='button'
                    className={`donations-month-chip ${
                      months === 12 ? 'donations-month-chip--featured' : ''
                    } ${
                      selectedMonths === months
                        ? 'donations-month-chip--active'
                        : ''
                    }`}
                    onClick={() => handleSelectMonths(months)}
                  >
                    <strong>{formatMonthLabel(months)}</strong>
                    <span>{formatPremiumPrice(getPremiumAmount(months))}</span>
                    {months === 12 && (
                      <small>
                        Invece di{' '}
                        {formatPremiumPrice(getBasePremiumAmount(months))}
                      </small>
                    )}
                  </button>
                ))}
              </div>

              <div className='donations-plan-slider'>
                <div className='form-info-row--between mb-2'>
                  <label
                    htmlFor='premiumMonths'
                    className='small text-light mb-0'
                  >
                    Durata abbonamento
                  </label>
                  <span className='badge bg-brand-secondary text-dark rounded-pill'>
                    {formatMonthLabel(selectedMonths)}
                  </span>
                </div>
                <input
                  id='premiumMonths'
                  type='range'
                  min={MIN_PREMIUM_MONTHS}
                  max={MAX_PREMIUM_MONTHS}
                  step={1}
                  value={selectedMonths}
                  onChange={event => handleSelectMonths(event.target.value)}
                  className='form-range'
                />
                <div className='d-flex justify-content-between small text-muted-brand'>
                  <span>{formatMonthLabel(MIN_PREMIUM_MONTHS)}</span>
                  <span>{formatMonthLabel(MAX_PREMIUM_MONTHS)}</span>
                </div>
              </div>

              <div className='donations-plan-summary'>
                <div className='donations-plan-summary__item'>
                  <span>Durata scelta</span>
                  <strong>{formatMonthLabel(selectedMonths)}</strong>
                </div>
                <div className='donations-plan-summary__item'>
                  <span>Tariffa</span>
                  <strong>
                    {isAnnualPlan
                      ? `${formatPremiumPrice(totalAmount)} / anno`
                      : `${formatPremiumPrice(MONTHLY_PREMIUM_PRICE)} / mese`}
                  </strong>
                </div>
                <div className='donations-plan-summary__item donations-plan-summary__item--total'>
                  <span>Totale</span>
                  <strong>{formatPremiumPrice(totalAmount)}</strong>
                </div>
                <div className='donations-plan-summary__item donations-plan-summary__item--savings'>
                  <span>Risparmio</span>
                  <strong>
                    {savingsAmount > 0
                      ? formatPremiumPrice(savingsAmount)
                      : 'Nessuno'}
                  </strong>
                </div>
              </div>
            </div>

            <div className='donations-presets'>
              <div className='donations-preset donations-preset--static'>
                <strong>
                  {isAnnualPlan
                    ? 'Premium Annuale consigliato'
                    : 'Premium personalizzato'}
                </strong>
                <span>{formatMonthLabel(selectedMonths)} selezionati</span>
                <small>
                  {isAnnualPlan
                    ? `Stai scegliendo la formula piu conveniente: 12 mesi a ${formatPremiumPrice(
                        totalAmount
                      )} con ${formatPremiumPrice(savingsAmount)} di sconto.`
                    : `Attivi il Premium per la durata che preferisci, sempre a ${formatPremiumPrice(
                        MONTHLY_PREMIUM_PRICE
                      )} al mese.`}
                </small>
              </div>
            </div>

            <button
              type='button'
              className='btn btn-lg btn-brand-secondary donations-panel__cta'
              onClick={handleCheckout}
              disabled={authLoading}
            >
              <i
                className={`bi ${
                  currentUser ? 'bi-paypal' : 'bi-box-arrow-in-right'
                } me-2`}
              ></i>
              {authLoading
                ? 'Verifica account...'
                : currentUser
                ? isAnnualPlan
                  ? 'Attiva l annuale scontato'
                  : `Attiva ${formatMonthLabel(selectedMonths)}`
                : 'Accedi per attivare il Premium'}
            </button>

            <p className='donations-panel__meta mb-0'>
              {currentUser
                ? `Pagamento pronto: ${paypalLink}`
                : 'Solo gli utenti registrati possono attivare il Premium. Se prosegui, ti reindirizziamo alla pagina di accesso.'}
            </p>

            {currentUser && (
              <div className='donations-premium-activation'>
                <a
                  href={activationMailto}
                  className='btn btn-outline-brand-secondary donations-premium-activation__mail'
                  onClick={() => setActivationMailClicked(true)}
                >
                  <i className='bi bi-envelope me-2'></i>
                  Ho completato il pagamento
                </a>
                {(paymentStarted || activationMailClicked) && (
                  <div className='donations-premium-activation__notice'>
                    <i className='bi bi-stars'></i>
                    <span>
                      Lo staff ti inserira tra gli utenti Premium appena
                      possibile. Riceverai una mail quando l'attivazione sara
                      completata.
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className='donations-auth-note'>
              {currentUser ? (
                <span>
                  <i className='bi bi-check-circle-fill me-2'></i>
                  Sei connesso: puoi completare il pagamento quando vuoi.
                </span>
              ) : (
                <span>
                  <i className='bi bi-shield-lock-fill me-2'></i>
                  Il pagamento e riservato agli utenti registrati.
                </span>
              )}
            </div>
          </div>
        </section>

        <section className='donations-story mt-4 mt-lg-5'>
          <div className='donations-story__card'>
            <p>
              <strong>Sostieni il Fantawrestling</strong>
            </p>

            <p>
              Il Fantawrestling nasce dalla passione di un gruppo di
              appassionati che ha voluto costruire qualcosa di speciale per
              tutta la community. Dietro ogni stagione, ogni classifica e ogni
              aggiornamento c&apos;e tempo, dedizione e anche qualche costo da
              sostenere: hosting, strumenti di gestione, grafiche, nuove
              funzionalità.
            </p>

            <p>
              Il progetto e restera sempre gratuito e aperto a tutti. Ma se ti
              stai divertendo con noi e vuoi dare una mano a farlo crescere,
              puoi contribuire con un <strong>abbonamento premium</strong> per
              il numero di mesi che preferisci.
            </p>

            <p>
              Ogni contributo viene reinvestito interamente nel progetto: sito
              piu veloce e stabile, nuove funzionalita, contenuti extra e tutte
              quelle piccole e grandi migliorie che rendono il Fantawrestling
              ogni stagione un po&apos; più bello.
            </p>

            <p>
              Il contributo consentira anche di sbloccare
              <strong> funzionalità premium </strong>
              dedicate a chi scegliera di sostenerci.
            </p>

            <p>
              <strong>Nessun obbligo, nessuna pressione.</strong> Solo chi vuole
              e puo, decide di farlo. Ed è un gesto che vale tantissimo per
              chi lavora dietro le quinte.
            </p>

            <p className='mb-0'>
              Puoi contribuire in totale sicurezza tramite PayPal cliccando sul
              pulsante qui sopra.
            </p>
          </div>

          <div className='donations-story__aside'>
            <div className='donations-note'>
              <h3>Funzionalità premium</h3>
              <ul className='donations-feature-list'>
                {premiumFeatures.map(feature => (
                  <li key={feature.title} className='donations-feature-item'>
                    <span className='donations-feature-item__icon'>
                      <i className='bi bi-stars' aria-hidden='true'></i>
                    </span>
                    <span className='donations-feature-item__body'>
                      <strong>{feature.title}</strong>
                      <small>{feature.description}</small>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className='donations-note'>
              <h3>Dove va il supporto</h3>
              <div className='donations-support-grid'>
                {supportAreas.map(item => (
                  <div key={item.title} className='donations-support-card'>
                    <div className='donations-support-card__icon'>
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <div>
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='donations-note donations-note--soft'>
              <p className='mb-0 text-muted-brand'>
                <em>
                  Grazie ancora per il supporto e per far parte della community
                  del Fantawrestling. Presto arriveranno nuove funzioni e
                  contenuti extra.
                </em>
              </p>
              <div className='d-flex justify-content-center gap-3 flex-wrap mt-4'>
                <a
                  href='https://t.me/fantawrestlingchat'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='px-3 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm faq-social-pill'
                  style={{
                    borderRadius: '2rem',
                    minWidth: 80,
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg, #2AABEE, #229ED9)',
                    color: 'black',
                    border: 'none',
                    textDecoration: 'none',
                    transition: 'var(--transition)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.transform = 'translateY(-3px)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.transform = 'translateY(0)')
                  }
                  title='Telegram'
                >
                  <i className='bi bi-telegram'></i> Telegram
                </a>
                <a
                  href='https://www.instagram.com/fanta.wrestling/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='px-3 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm faq-social-pill'
                  style={{
                    borderRadius: '2rem',
                    minWidth: 80,
                    fontSize: '1rem',
                    background:
                      'linear-gradient(135deg, #5851DB, #C13584, #E1306C, #F56040, #FCAF45)',
                    color: '#fff',
                    border: 'none',
                    textDecoration: 'none',
                    transition: 'var(--transition)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.transform = 'translateY(-3px)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.transform = 'translateY(0)')
                  }
                  title='Instagram'
                >
                  <i className='bi bi-instagram'></i> Instagram
                </a>
                <a
                  href='mailto:fantafwi@gmail.com'
                  className='px-3 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm faq-social-pill'
                  style={{
                    borderRadius: '2rem',
                    minWidth: 80,
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg, #b81414, #f53939)',
                    color: 'white',
                    border: 'none',
                    textDecoration: 'none',
                    transition: 'var(--transition)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.transform = 'translateY(-3px)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.transform = 'translateY(0)')
                  }
                  title='Email'
                >
                  <i className='bi bi-envelope'></i> Email
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          className='donations-showcase mt-4 mt-lg-5'
          aria-labelledby='premium-showcase-title'
        >
          <div className='donations-showcase__intro'>
            <span className='donations-eyebrow'>
              <i className='bi bi-stars me-2'></i>
              Anteprime reali
            </span>
            <h2 id='premium-showcase-title'>Cosa ottieni con il Premium</h2>
            <p>
              Una carrellata di schermate reali per capire subito cosa sblocchi:
              aiuto scambi, suggerimenti asta, divisioni wrestler e insight
              avanzati.
            </p>
          </div>

          <div className='donations-showcase__viewport'>
            <div className='donations-showcase__track'>
              {[...premiumShowcaseSlides, ...premiumShowcaseSlides].map(
                (slide, index) => (
                  <article
                    key={`${slide.title}-${index}`}
                    className='donations-showcase__slide'
                    aria-hidden={index >= premiumShowcaseSlides.length}
                  >
                    <div className='donations-showcase__image-shell'>
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className='donations-showcase__image'
                        loading='lazy'
                      />
                    </div>
                    <div className='donations-showcase__caption'>
                      <strong>{slide.title}</strong>
                      <span>{slide.description}</span>
                    </div>
                  </article>
                )
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Premium
